import React, {memo, useEffect, useRef, useState} from "react";
import {AnimatePresence, motion} from "framer-motion";
import {ClipboardPaste, Plus} from "lucide-react";
import {FlowMode, WorkflowNodeTypes} from "@/types/node";
import {WorkflowNodesData} from "../NodeTypes/NodesData";
import {Edge, useReactFlow} from "@xyflow/react";
import {useSave} from "@/contexts/SaveContext";
import {FlowBuilderSettings} from "@/config/flowBuilderSettings";
import {useCopyPasteContext} from "@/contexts/CopyPasteContext";
import {getTailwindColors} from "@/constants/tailwindConfigs";

interface NewNodeHandleProps {
  id: string;
  onNewNodeSelect: (blueprint: any) => void;
  selectedEdge: Edge;
}

const NewNodeHandle: React.FC<NewNodeHandleProps> = ({
  id,
  onNewNodeSelect,
  selectedEdge,
}) => {
  const {handleSave, flowMode} = useSave();
  const {bufferedNodes} = useCopyPasteContext();
  const {getNode, getEdges} = useReactFlow();
  const TailwindColors = getTailwindColors();

  const menuRef = useRef<HTMLDivElement>(null);
  const isDemoMode = FlowBuilderSettings.demoMode;

  const edge = getEdges().find(edge => edge.id === id);
  const [isPasteAvailable, setIsPasteAvailable] = useState(
    bufferedNodes.length > 0,
  );
  const isMenuVisible = id === selectedEdge?.id;

  const targetNode = getNode(edge?.target) || null;
  const sourceNode = getNode(edge?.source) || null;

  const isTargetPlaceholder =
    targetNode?.type === WorkflowNodeTypes.PLACEHOLDER;
  const isSourceStart = sourceNode?.type === WorkflowNodeTypes.START;

  const defaultNodeTypes = [
    WorkflowNodeTypes.BRANCH,
    WorkflowNodeTypes.STEP,
  ].filter(Boolean);

  const availableNodeTypes = [...defaultNodeTypes];

  if (isTargetPlaceholder && !isSourceStart) {
    availableNodeTypes.push(WorkflowNodeTypes.JUMP);
    availableNodeTypes.push(WorkflowNodeTypes.END);
  }

  const availableNodes = Object.fromEntries(
    Object.entries(WorkflowNodesData)
      .filter(([key]) => availableNodeTypes.includes(key as WorkflowNodeTypes))
      .sort(
        ([keyA], [keyB]) =>
          availableNodeTypes.indexOf(keyA as WorkflowNodeTypes) -
          availableNodeTypes.indexOf(keyB as WorkflowNodeTypes),
      ),
  );

  useEffect(() => {
    setIsPasteAvailable(bufferedNodes.length > 0);
  }, [bufferedNodes]);

  availableNodes.PASTE = {
    name: "Paste",
    blueprint: {},
    icon: ClipboardPaste,
    styles: {
      iconColor: TailwindColors.violet.DEFAULT,
    },
  };

  useEffect(() => {
    // Disable paste if the target is not a placeholder and the copied node is a jump
    const nodeToPasteType = bufferedNodes?.[0]?.type;
    if (
      nodeToPasteType === WorkflowNodeTypes.JUMP ||
      nodeToPasteType === WorkflowNodeTypes.END
    )
      setIsPasteAvailable(isTargetPlaceholder);
  }, [isTargetPlaceholder, bufferedNodes]);

  if (flowMode === FlowMode.LIVE) return null;

  return (
    <div className="w-8 border-2 border-[#fafafa] border-transparent bg-transparent absolute flex items-center flex-col h-auto gap-0.5 z-10 cursor-auto transform-none -top-4 -left-4 pointer-events-auto rounded-md group">
      {/* Line cover span */}
      <span className="absolute top-0 bottom-0 m-auto w-1 h-full bg-[#fafafa] z-0"></span>
      <span className="absolute top-0 bottom-0 m-auto w-full h-1 bg-[#fafafa] z-0"></span>
      <div
        className={`w-8 h-8 z-10 shrink-0 border border-border text-2xl text-secondary flex items-center justify-center cursor-pointer rounded-md transition-all group-hover:transition-all group-hover:bg-white group-hover:rounded-3xl group-hover:border-border ${isTargetPlaceholder ? "bg-white" : "border-transparent bg-transparent"} `}
      >
        <Plus size="18" />
      </div>

      <AnimatePresence>
        {isMenuVisible && (
          <motion.div
            key="settings"
            initial={{opacity: 0, scale: 0.95}}
            animate={{opacity: 1, scale: 1}}
            exit={{opacity: 0, scale: 0.95}}
            transition={{duration: 0.1, ease: "easeInOut"}}
            className="absolute z-100 left-full top-1/2 !-translate-y-1/2 ml-3"
            ref={menuRef}
          >
            <div className="bg-white border border-border shadow-lg rounded-md p-1">
              <div className="absolute right-full top-1/2 -translate-y-1/2 -left-1 w-2 h-2 bg-white rotate-45 border-b border-l border-border"></div>
              {Object.entries(availableNodes).map(([type, node], index) => {
                const isPaste = type === "PASTE";
                if (isPaste && !isPasteAvailable) return null;

                return (
                  <div
                    key={type}
                    onClick={() => {
                      if (isDemoMode) return;

                      if (!isPaste || isPasteAvailable) {
                        onNewNodeSelect(
                          isPaste ? bufferedNodes[0] : node.blueprint,
                        );
                      }
                      handleSave();
                    }}
                    className={`w-30 text-sm flex items-center gap-2 cursor-pointer px-2 py-1.5 rounded-md  ${isDemoMode ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-50"}`}
                  >
                    <node.icon
                      className={`w-4 h-4`}
                      color={node.styles.iconColor}
                    />
                    {node.name}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default memo(NewNodeHandle);
